import { useMemo, useCallback } from 'react';
import { Tag, Tooltip, Button, message } from 'antd';
import { LockOutlined, PrinterOutlined } from '@ant-design/icons';
import { ErpDataGrid } from '../../../../components/common/erp-grid';
import { workOrdersApi } from './workOrdersApi';
import { pieceStateLabel, pieceStateColor, pieceKindLabel, pieceIndexLabel } from './workOrderHelpers';

// ════════════════════════════════════════════════════════════════════
// Stage WO-LINES-GRID — "All pieces" register (scale companion to the lines grid).
//
// The lines grid nests each line's pieces on expand, which is great for browsing
// but not for finding ONE barcode among thousands. This is the flat, paginated,
// SEARCHABLE, exportable piece register (barcode / stage search + CSV export) —
// ErpDataGrid paginates client-side so only one page renders at a time regardless
// of piece count. Read-only.
// ════════════════════════════════════════════════════════════════════

export default function WorkOrderPiecesGrid({ pieces, lines, woNo, canPrint = false, onReprinted }) {
  const pieceList = Array.isArray(pieces) ? pieces : [];
  const lineList = Array.isArray(lines) ? lines : [];

  // Reprint ONE piece's label — reuses the existing single-sticker endpoint
  // (workOrdersApi.pieceSticker → GET /pieces/:barcode/sticker), the same path
  // the Scan Station uses. Opens the rendered HTML in a new tab for printing.
  // WO-3.3 — when the piece had a "reprint due" flag (a Change Order re-tuned its
  // spec), clear it after a successful print and ask the parent to reload so the
  // badge disappears. Clearing is best-effort — the label already printed.
  const printSticker = useCallback(async (barcode, due = false) => {
    if (!barcode) return;
    try {
      const html = await workOrdersApi.pieceSticker(barcode);
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(url, '_blank');
      if (!win) message.warning('Allow pop-ups to print the sticker.');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (due) {
        try {
          await workOrdersApi.clearPieceReprint(barcode);
          if (typeof onReprinted === 'function') onReprinted();
        } catch (_e) { /* non-fatal — the sticker still printed */ }
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message)
        || (e && e.message) || 'Could not load the sticker.';
      message.error(msg);
    }
  }, [onReprinted]);

  const lineMetaById = useMemo(() => {
    const m = new Map();
    lineList.forEach((l) => {
      const s = l.specSnapshot || {};
      m.set(l.id, {
        lineNo: l.lineNo,
        thicknessMm: s.thicknessMm != null ? Number(s.thicknessMm) : null,
        componentCount: l.componentCount != null ? Number(l.componentCount) : null
      });
    });
    return m;
  }, [lineList]);

  const unitIndexById = useMemo(() => {
    const m = new Map();
    pieceList.forEach((p) => { if (p.kind === 'UNIT') m.set(p.id, { index: p.index, total: p.total }); });
    return m;
  }, [pieceList]);

  const columns = useMemo(() => {
    const cols = [
    { title: '#', key: '__sno__', width: 52, align: 'center',
      render: (_v, _r, idx) => <span className="erp-grid__muted">{idx + 1}</span> },
    { title: 'Barcode', dataIndex: 'barcode', key: 'barcode', width: 170, fixed: 'left',
      sorter: (a, b) => String(a.barcode || '').localeCompare(String(b.barcode || '')),
      render: (v) => v ? <code className="erp-grid__code">{v}</code> : null },
    { title: 'Line', key: 'line', width: 60, align: 'center',
      render: (_v, r) => <span className="tabular-nums">{lineMetaById.get(r.lineId)?.lineNo ?? '—'}</span> },
    { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 110, align: 'center', dataType: 'enum',
      render: (v) => <Tag className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{v === 'COMPONENT' ? 'Pane' : pieceKindLabel(v)}</Tag> },
    { title: 'Piece', key: 'index', width: 108, align: 'center',
      render: (_v, r) => {
        if (r.kind === 'UNIT') return <span className="tabular-nums">{r.index} of {r.total}</span>;
        if (r.kind === 'COMPONENT') {
          const u = r.parentUnitId ? unitIndexById.get(r.parentUnitId) : null;
          return <span className="tabular-nums">{u ? `U${u.index} · ` : ''}{r.index}/{r.total}</span>;
        }
        return <span className="tabular-nums">{pieceIndexLabel(r.index, r.total)}</span>;
      } },
    { title: 'Glass', key: 'glass', width: 160, ellipsis: true,
      render: (_v, r) => {
        if (r.kind === 'UNIT') {
          const n = lineMetaById.get(r.lineId)?.componentCount;
          return <span className="erp-grid__muted">Assembly{n ? ` · ${n} panes` : ''}</span>;
        }
        const thk = r.paneThicknessMm != null ? r.paneThicknessMm : lineMetaById.get(r.lineId)?.thicknessMm;
        return (
          <div style={{ lineHeight: 1.3 }}>
            <span className="tabular-nums">{thk != null ? `${thk} mm` : '—'}</span>
            {r.paneItemDesc ? <div className="truncate" style={{ fontSize: 11, color: '#64748b' }}>{r.paneItemDesc}</div> : null}
          </div>
        );
      } },
    { title: 'Current stage', dataIndex: 'currentStage', key: 'currentStage', width: 140,
      render: (v) => v ? <Tag className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{v}</Tag> : <span className="erp-grid__muted">—</span> },
    { title: 'State', dataIndex: 'state', key: 'state', width: 120, align: 'center', dataType: 'enum',
      groupable: true, groupField: 'state',
      render: (v) => <Tag color={pieceStateColor(v)} className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{(pieceStateLabel(v) || '').toUpperCase()}</Tag> },
    { title: 'Locked', dataIndex: 'locked', key: 'locked', width: 76, align: 'center', emptyFallback: false,
      render: (v) => v ? <Tooltip title="Spec locked at cutting"><LockOutlined style={{ color: '#64748b' }} /></Tooltip> : <span className="erp-grid__muted">—</span> }
    ];
    // Sticker column — ALWAYS present so a "Reprint due" badge is visible even to
    // a viewer without print rights (a Change Order re-tuned this already-cut
    // piece's spec, so its printed label no longer matches what is billed). The
    // reprint ACTION (highlighted when due) is gated on print RBAC, matching the
    // bulk stickers. Excluded from CSV export; pinned right like an action column.
    cols.push({
      title: 'Sticker', key: '__sticker__', width: 128, align: 'center', fixed: 'right',
      exportable: false, emptyFallback: false,
      render: (_v, r) => {
        const due = r.needsStickerReprint === true;
        return (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {due ? (
              <Tooltip title={r.stickerReprintReason === 'CHANGE_ORDER_RESPEC'
                ? 'A Change Order re-tuned this piece — reprint its sticker to match the billed spec.'
                : 'This piece’s spec changed — reprint its sticker.'}>
                <Tag color="warning" className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>Reprint due</Tag>
              </Tooltip>
            ) : null}
            {canPrint ? (
              <Tooltip title={r.barcode ? (due ? 'Reprint this sticker' : 'Print this sticker') : 'No barcode yet'}>
                <Button size="small" type={due ? 'primary' : 'text'} danger={due}
                  icon={<PrinterOutlined />} disabled={!r.barcode}
                  onClick={() => printSticker(r.barcode, due)} aria-label="Print sticker" />
              </Tooltip>
            ) : (due ? null : <span className="erp-grid__muted">—</span>)}
          </div>
        );
      }
    });
    return cols;
  }, [lineMetaById, unitIndexById, canPrint, printSticker]);

  const pageSizeOptions = useMemo(() => ['25', '50', '100', '200'], []);

  return (
    <ErpDataGrid
      title=""
      gridId="production-work-order-pieces-v1"
      columns={columns}
      dataSource={pieceList}
      rowKey="id"
      rowHeight="compact"
      stickyHeader
      pageSize={50}
      pageSizeOptions={pageSizeOptions}
      searchable
      searchPlaceholder="Search by barcode / stage…"
      exportable
      exportFilename={`work-order-${woNo || 'pieces'}-pieces`}
      emptyTitle="No pieces"
      emptyText="This work order has no pieces."
    />
  );
}
